import React, { useEffect, useState, useCallback } from 'react';
import { usePrefs, useLabels, useRequestData } from 'wsm-common-data';
import { LocationModal } from 'wsm-location-modal';
import { useDispatch, useSelector } from 'react-redux';
import { API } from '../api/services';
import { getDistance } from '../utils/utility';
import { useLocationState } from '../features/location';
import { setDistance } from '../features/distance';

const Distance = () => {
	const dispatch = useDispatch();
	const { distance, userZip } = useSelector((state) => state.distance);

	const prefs = usePrefs();
	const labels = useLabels();
	const { locale } = useRequestData();
	const { vehicleLat, vehicleLong } = useLocationState();

	const showDistance = prefs.showDistanceFromShopper === 'true';
	const showDelivery = prefs.deliveryEnabled === 'true';
	const enableNewUI = prefs.legacyStyling === 'false';

	const { deviceType } = useRequestData();
	const isMobile = deviceType === 'MOBILE';
	const [showLocationModal, setShowLocationModal] = useState(false);

	const fetchDistance = useCallback(
		async (postalCode, userLatitude, userLongitude) => {
			let result;
			if (postalCode) {
				result = await API.getDistanceFromLS(
					postalCode,
					vehicleLat,
					vehicleLong,
					locale
				);
				if (result?.distance) {
					dispatch(
						setDistance({
							distance: result.distance,
							userZip: postalCode
						})
					);
					return;
				}
			}
			if (userLatitude && userLongitude) {
				result = Math.round(
					getDistance(
						userLatitude,
						userLongitude,
						vehicleLat,
						vehicleLong,
						locale
					)
				);
				dispatch(
					setDistance({ distance: result, userZip: postalCode })
				);
				return;
			}

			// Fallback if no distance is found
			dispatch(setDistance({ distance: null, userZip: postalCode }));
		},
		[vehicleLat, vehicleLong, locale, dispatch]
	);

	useEffect(() => {
		if (!distance && (showDistance || showDelivery)) {
			const sessionStorageLocation = JSON.parse(
				window?.sessionStorage?.getItem('session.location')
			);
			const userPostalCode =
				window?.localStorage?.getItem('userLocation') ||
				sessionStorageLocation?.userPostalCode;
			const { userLatitude, userLongitude } =
				sessionStorageLocation || {};

			fetchDistance(userPostalCode, userLatitude, userLongitude);
		}
	}, [distance, showDistance, showDelivery, fetchDistance]);

	const getLabeledValue = (label, userPostalCode) => {
		return labels
			.get(label)
			.replace(/\{\{distance\}\}/, distance)
			.replace(/\{\{location\}\}/, userPostalCode)
			.trim();
	};

	const onCloseLocationModal = () => {
		setShowLocationModal(false);
	};

	const onChangeUserPostalCode = (postalCode) => {
		const sessionStorageLocation = JSON.parse(
			window?.sessionStorage?.getItem('session.location')
		);
		const { userLatitude, userLongitude } = sessionStorageLocation || {};
		fetchDistance(postalCode, userLatitude, userLongitude);
	};

	if (!showDistance) return null;

	if (enableNewUI) {
		const showDistanceLabel = userZip && distance;
		return (
			<>
				<span className="ddc-font-size-xsmall mr-3 pr-2 text-muted d-flex align-items-center">
					{showDistanceLabel
						? getLabeledValue('DISTANCE_FROM_LOCATION', '')
						: null}
					<button
						type="button"
						className={`btn-link text-muted mr-0 text-decoration-underline text-link ${
							showDistanceLabel
								? 'pl-1 ml-1'
								: 'pl-0 ml-0 border-left-0'
						}`}
						onClick={() => setShowLocationModal(true)}
						aria-label={`${userZip || ''}, ${labels.get(
							'PLEASE_ENTER_YOUR_ZIP_CODE'
						)}`}
					>
						{userZip || labels.get('PLEASE_ENTER_YOUR_ZIP_CODE')}
					</button>
				</span>
				<LocationModal
					userPostalCode={userZip}
					isOpen={showLocationModal}
					onClose={onCloseLocationModal}
					isMobile={isMobile}
					title={labels.get('YOUR_LOCATION')}
					onChangeUserPostalCode={onChangeUserPostalCode}
				/>
			</>
		);
	}

	if (distance) {
		return (
			<div>
				<span className="ddc-font-size-xsmall">
					{getLabeledValue('DISTANCE_FROM_LOCATION', userZip)}
				</span>
			</div>
		);
	}

	return null;
};

export default Distance;
